import React from 'react';
import { useTranslation } from 'localization';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import type { GlyphColor } from '../../../types/glyph';

// Discover-safe author block. When present, the reviewer's avatar + name are shown;
// otherwise the card falls back to the plain author name / anonymous label.
export interface ReviewCardAuthor {
  subjectSphereId: string | null;
  displayName: string;
  glyph: string | null;
  glyphColor: GlyphColor | null;
  imageId: string | null;
}

export interface ReviewCardMoleculeProps {
  stars: number;
  title?: string | null;
  body: string;
  authorName?: string | null;
  author?: ReviewCardAuthor | null;
  date: string;
  isEdited?: boolean;
  className?: string;
}

const ReviewCardMolecule: React.FC<ReviewCardMoleculeProps> = ({
  stars,
  title,
  body,
  authorName,
  author,
  date,
  isEdited = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const formattedDate = new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const displayAuthor = author?.displayName || authorName || t('marketplace.reviews.anonymous');

  const classes = ['marketplace-review-card', className].filter(Boolean).join(' ');

  return (
    <article className={classes}>
      <div className="marketplace-review-card__header">
        <StarDisplayAtom rating={stars} size="small" />
        <span className="marketplace-review-card__date">
          {formattedDate}
          {isEdited && (
            <span className="marketplace-review-card__edited">
              {' '}
              ({t('marketplace.reviews.editedLabel')})
            </span>
          )}
        </span>
      </div>
      {title && (
        <h4 className="marketplace-review-card__title">{title}</h4>
      )}
      <p className="marketplace-review-card__body">{body}</p>
      {author ? (
        <SphereIdentityAtom
          sphereId={author.subjectSphereId ?? undefined}
          sphereName={author.displayName}
          glyph={author.glyph ?? undefined}
          glyphColor={author.glyphColor ?? undefined}
          imageId={author.imageId ?? undefined}
          size="xsmall"
          className="marketplace-review-card__author-identity"
        />
      ) : (
        <span className="marketplace-review-card__author">
          {t('marketplace.reviews.byAuthor', { author: displayAuthor })}
        </span>
      )}
    </article>
  );
};

export default ReviewCardMolecule;
